import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import T from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Button, Divider, FormGroup, Grid, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormElements, TimezoneGMT, TIMEZONES_GMT_NAMES } from 'web-components';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import AddEditBatchDialog from 'components/BatchRecordsOverview/AddEditBatchDialog';
import DeleteBatchDialog from 'components/BatchRecordsOverview/DeleteBatchDialog';
import { getOnlyNotifyRole } from 'helpers/role';
import { ROLE_MANAGER } from 'attrs/roles';
import { convertChartTemplate2Bas64, generateFooterImage } from 'helpers/chartUtils';
import { StyledDescription, StyledDialog, StyledDialogBody, StyledDialogHeader } from './ReportExportDialog.Styled';
import { COLOR_PRIMARY } from '../../attrs/colors';

import {
  StyledDialogActions,
  StyledDialogSubHeader
} from '../MachinesOverviewDetails/EditMachineDialog/EditMachineDialog.Styled';

import { getShowReportExportModal } from '../../redux/batch_export/selectors';
import { putRequesBatchReportExport, showBatchReportExportModal } from '../../redux/batch_export/actions';
import { initialReportExportValues } from '../../redux/batch_export/constants';
import { getUserInfo, getUserRole } from '../../redux/user/selectors';
import { getFormikError, getFormikHelperText, getUserFeature } from '../../helpers/utils';
import TimeInputList from '../TimeInputList';
import { FileFormatsBatches } from '../../helpers/consts';

const ReportExportDialog = ({ batch, chartRef, footerRef }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { user } = useSelector(getUserInfo);

  const { isOpen } = useSelector(getShowReportExportModal);

  const [isLoading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);

  const [openAddEditBatch, setOpenAddEditBatch] = useState(false);

  const [openDeleteBatch, setOpenDeleteBatch] = useState(false);

  const initialValues = { ...initialReportExportValues };

  const FILTERED_OUT_TIMEZONES = [TIMEZONES_GMT_NAMES.UNKNOWN];

  const timezones = TimezoneGMT.filter(({ value }) => !FILTERED_OUT_TIMEZONES.includes(value));

  // user permissions
  const role = useSelector(getUserRole);
  const [permissions, setPermissions] = useState('');

  useEffect(() => {
    if (role) {
      const oneRole = getOnlyNotifyRole(role);
      setPermissions(oneRole);
    }
  }, [role]);

  const periods = [
    { key: '5s', value: '5 s' },
    { key: '30s', value: '30 s' },
    { key: '1m0s', value: '1 min' },
    { key: '1h0m0s', value: '1 h' },
    { key: '24h0m0s', value: '1 d' }
  ];

  const normalizeValuesForActions = async values => {
    const { action } = values;
    const updateValue = values;

    if (action === 'csv') {
      updateValue.graphs = null;

      return updateValue;
    }

    const chartInstance = chartRef?.current?.getEchartsInstance();
    let chartPng = '';
    let footerPng = '';
    if (chartInstance && footerRef?.current) {
      chartPng = convertChartTemplate2Bas64(chartInstance);
      footerPng = await generateFooterImage(footerRef);
    }

    updateValue.graphs = [
      {
        name: 'chart',
        image: [chartPng]
      },
      {
        name: 'footer',
        image: [footerPng]
      }
    ];

    return updateValue;
  };

  const handleSubmitForm = async values => {
    setLoading(true);
    const normalizedValue = await normalizeValuesForActions(values);

    dispatch(putRequesBatchReportExport(batch?.details?.id, normalizedValue));
    setTimeout(() => setLoading(false), 1000);
  };

  const handleMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClickOpenAddEditBatch = () => {
    handleClose();
    // exec the open function in the next tick, to make sure the menu is closed before opening the dialog
    // to avoid: Maximum call stack size exceeded
    setTimeout(() => setOpenAddEditBatch(true), 0);
  };

  const onClickOpenDeleteBatch = () => {
    handleClose();
    setTimeout(() => setOpenDeleteBatch(true), 0);
  };

  const handleOpenReportExportModal = open => {
    handleClose();
    setTimeout(() => dispatch(showBatchReportExportModal(open)), 0);
  };

  const handleCloseReportExportModal = () => () => {
    handleOpenReportExportModal(false);
  };

  return (getUserFeature(user.data, 'BATCH_RECORDS') || { enabled: false }).enabled ? (
    <>
      <Button
        id="batch-details-btn-menu-options"
        data-testid="batch-details-btn-menu-options"
        variant="contained"
        sx={{ height: '56px !important', width: '30px !important' }}
        onClick={handleMenu}
      >
        <MoreHorizIcon sx={{ fontSize: '30px !important' }} />
      </Button>
      <Menu
        id="batch-details-menu-options"
        data-testid="batch-details-menu-options"
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && !openAddEditBatch}
        onClose={handleClose}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={{ mt: 1 }}
        PaperProps={{
          style: {
            width: 200
          }
        }}
      >
        <MenuItem onClick={onClickOpenAddEditBatch} data-testid="batch-details-btn-edit">
          <ListItemIcon>
            <ModeEditOutlineOutlinedIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('default_actions.edit')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleOpenReportExportModal(true)} data-testid="batch-details-btn-export-data">
          <ListItemIcon>
            <DownloadOutlinedIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('batch_records.export_batch')}</ListItemText>
        </MenuItem>
        {ROLE_MANAGER === permissions && batch?.origin === 'MANUAL' && (
          <>
            <Divider />
            <MenuItem onClick={onClickOpenDeleteBatch} data-testid="batch-details-btn-delete">
              <ListItemIcon>
                <DeleteForeverOutlinedIcon fontSize="small" sx={{ color: '#C12907' }} />
              </ListItemIcon>
              <ListItemText>{t('default_actions.delete')}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
      <AddEditBatchDialog open={openAddEditBatch} setOpen={setOpenAddEditBatch} initialBatchRecord={batch} />
      <DeleteBatchDialog open={openDeleteBatch} setOpen={setOpenDeleteBatch} batchId={batch?.details?.id} />
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Formik
          initialValues={initialValues}
          onSubmit={values => {
            handleSubmitForm(values);
          }}
          enableReinitialize={false}
        >
          {({ values, handleSubmit, errors, setFieldValue, handleBlur, touched, handleChange }) => {
            const getError = getFormikError({ errors, touched });
            const getHelperText = getFormikHelperText({ errors, t });

            return (
              <StyledDialog open={isOpen}>
                <StyledDialogHeader
                  theme={theme}
                  highlightColor={COLOR_PRIMARY}
                  closeButtonProps={{ color: 'primary' }}
                  onClose={() => handleOpenReportExportModal(false)}
                >
                  {t('dialog.batch_record.title')}
                </StyledDialogHeader>
                <StyledDialogSubHeader theme={theme} withDivider={false} narrow>
                  {t('dialog.batch_record.subtitle')}
                </StyledDialogSubHeader>
                <StyledDialogBody>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <FormGroup row>
                        <FormElements.Select
                          data-testid="batch-records-export-report-file-format-select"
                          error={getError('action')}
                          helperText={getHelperText('action')}
                          handleChange={handleChange}
                          id="action"
                          label={t('dialog.batch_record.select_file_format')}
                          name="action"
                          onBlur={handleBlur}
                          value={values.action}
                          fullWidth
                        >
                          {FileFormatsBatches.map(file => (
                            <option key={file.key} value={file.value}>
                              {file.name}
                            </option>
                          ))}
                        </FormElements.Select>
                      </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <FormGroup row>
                        <TimeInputList
                          data-testid="batch-records-export-report-sample-period-select"
                          notTime
                          handleTimeChange={value => setFieldValue('window', value)}
                          value={values.window}
                          labelText="dialog.batch_record.sample_period"
                          fieldId="sample_period"
                          autoComplete={false}
                          options={periods}
                          onBlur={handleBlur}
                          handleBlur={handleBlur}
                          errors={errors}
                          error={errors.time_from && touched.time_from}
                          helperText={errors.time_from && touched.time_from && errors.time_from}
                          touched={touched}
                          forceValue
                          getError={getError}
                          getHelperText={getHelperText}
                        />
                        <StyledDescription>{t('dialog.batch_record.period_description')}</StyledDescription>
                      </FormGroup>
                    </Grid>
                  </Grid>

                  {values.action === 'CSV' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={6} lg={6}>
                        <FormGroup row>
                          <FormElements.Select
                            data-testid="batch-records-export-report-csv-column-separator"
                            handleChange={handleChange}
                            id="csv_column_separator"
                            label={t('dialog.batch_record.csv_column_separator')}
                            name="csv_column_separator"
                            onBlur={handleBlur}
                            value={values.csv_column_separator}
                            fullWidth
                          >
                            <option key="semicolon" value=";">
                              ;
                            </option>
                            <option key="comma" value=",">
                              ,
                            </option>
                          </FormElements.Select>
                        </FormGroup>
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <FormGroup row>
                      <FormElements.Select
                        data-testid="batch-records-export-report-timezone-select"
                        error={getError('timeZone')}
                        helperText={getHelperText('timeZone')}
                        handleChange={handleChange}
                        id="timeZone"
                        label={t('dialog.data_exports.timezone')}
                        name="timeZone"
                        onBlur={handleBlur}
                        value={values.timeZone}
                      >
                        {timezones.map(gmt => (
                          <option key={gmt.value} value={gmt.value}>
                            {gmt.name}
                          </option>
                        ))}
                      </FormElements.Select>
                    </FormGroup>
                  </Grid>
                </StyledDialogBody>

                <StyledDialogActions>
                  <Button
                    data-testid="batch-records-export-report-cancel-button"
                    variant="text"
                    color="secondary"
                    onClick={handleCloseReportExportModal()}
                  >
                    {t('dashboard.custom.cancel')}
                  </Button>
                  <FormElements.LoadingButton
                    data-testid="batch-records-export-report-confirm-button"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    type="submit"
                    disabled={Object.keys(errors).length !== 0 || isLoading}
                    loading={isLoading}
                  >
                    {t('dialog.batch_record.export')}
                  </FormElements.LoadingButton>
                </StyledDialogActions>
              </StyledDialog>
            );
          }}
        </Formik>
        {}
      </LocalizationProvider>
    </>
  ) : (
    <></>
  );
};

ReportExportDialog.propTypes = {
  batch: T.shape().isRequired,
  chartRef: T.shape(),
  footerRef: T.shape()
};

ReportExportDialog.defaultProps = {
  chartRef: null,
  footerRef: null
};

export default React.memo(ReportExportDialog);
